import React, { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Input } from "../../../ui/primitives/Input";
import { Modal } from "../../../ui/primitives/Modal";
import { RadioGroup } from "../../../ui/primitives/Radio";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import { useRotaService } from "../../../services/members/RotaServiceProvider";
import { RotaSlot } from "../../../model/members/Rota";
import { formatShiftWindow } from "../../../utils/rotaUtils";

// The work being allocated is date-keyed even though the rota slot is not: the shift
// recurs weekly, but "deep clean studio 2" belongs to one specific day.
export interface AddWorkTarget {
  slot: RotaSlot;
  date: string; // YYYY-MM-DD
}

interface AddWorkModalProps {
  target: AddWorkTarget | null;
  onClose: () => void;
  onSaved: () => void;
}

// "Sales" is a UI-only routing choice — it hands off to the CRM assign-calls flow rather
// than persisting an allocation here, so the picker's type is wider than AllocationKind.
type WorkType = "Task" | "Sales" | "BizDev";

const WORK_TYPES = [
  { label: "Task", value: "Task" },
  { label: "Sales", value: "Sales" },
  { label: "Business Development", value: "BizDev" },
];

const AddWorkModal: React.FC<AddWorkModalProps> = ({ target, onClose, onSaved }) => {
  const service = useRotaService();
  const navigate = useNavigate();

  const [workType, setWorkType] = useState<WorkType>("Task");
  const [taskTitle, setTaskTitle] = useState<string>("");
  const [bdFocus, setBdFocus] = useState<string>("");
  const [bdTarget, setBdTarget] = useState<string>("");
  // One brief for both types: it is the same idea either way, and keeping a single piece of
  // state means switching Task <-> Business Development doesn't lose what the manager has already typed.
  const [brief, setBrief] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  // Reset whenever a different shift is opened, so yesterday's half-typed task doesn't
  // reappear against someone else's shift.
  useEffect(() => {
    if (!target) return;
    setWorkType("Task");
    setTaskTitle("");
    setBdFocus("");
    setBdTarget("");
    setBrief("");
  }, [target]);

  const submit = useCallback(async () => {
    // FORT-370: allocate to whoever is actually on the shift. Allocating to the rostered
    // person when someone else is covering hands the work to somebody who isn't there.
    //
    // Resolved BEFORE the guard, not after: an unassigned slot that is covered has a null
    // staffId but a real effectiveStaffId, and guarding on staffId first made the modal
    // return silently on exactly the slot the rota had just offered "Add work" on.
    //
    // `== null` rather than a falsy check — an id of 0 is not "no staff member" (see the
    // null/zero rule in apps/admin-web/CLAUDE.md).
    if (target == null) return;
    const staffId = target.slot.effectiveStaffId ?? target.slot.staffId;
    if (staffId == null) return;

    if (workType === "Sales") {
      // Sales work is a batch of leads picked in the CRM, so this routes there rather
      // than creating anything here.
      navigate("/members/retention");
      onClose();
      return;
    }

    setSaving(true);
    try {
      if (workType === "Task") {
        const title = taskTitle.trim();
        if (!title) {
          message.error("A task title is required.");
          return;
        }
        await service.createTaskAllocation({
          staffId,
          date: target.date,
          title,
          brief: brief.trim() || null,
        });
      } else {
        const focusArea = bdFocus.trim();
        if (!focusArea) {
          message.error("A focus area is required.");
          return;
        }
        await service.createBizDevAllocation({
          staffId,
          date: target.date,
          focusArea,
          target: bdTarget.trim() || null,
          brief: brief.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch {
      // The API client already surfaces the failure as a notification; re-reporting it
      // here would double-toast. Swallowing keeps the modal open so the work isn't lost.
    } finally {
      setSaving(false);
    }
  }, [target, workType, taskTitle, bdFocus, bdTarget, brief, service, navigate, onClose, onSaved]);

  const title = target
    ? `Add work — ${
        (target.slot.isCovered ? target.slot.coveringStaffName : target.slot.staffName) ?? "Unassigned"
      }, ${dayjs(target.date).format("ddd D MMM")} (${formatShiftWindow(target.slot)})`
    : "Add work";

  return (
    <Modal
      open={target != null}
      title={title}
      onClose={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={workType === "Sales" ? "Go to CRM" : "Add"}
      data-testid="add-work-modal"
    >
      <Stack gap={4}>
        <RadioGroup
          value={workType}
          onChange={(value) => setWorkType(value as WorkType)}
          options={WORK_TYPES}
          optionType="button"
          buttonStyle="solid"
          data-testid="add-work-type"
        />

        {workType === "Task" ? (
          <Input
            placeholder="e.g. Deep clean studio 2"
            value={taskTitle}
            data-testid="add-work-task-title"
            onChange={(e) => setTaskTitle(e.target.value)}
          />
        ) : null}

        {workType === "BizDev" ? (
          <>
            <Input
              placeholder="Focus area (e.g. Corporate memberships)"
              value={bdFocus}
              data-testid="add-work-bd-focus"
              onChange={(e) => setBdFocus(e.target.value)}
            />
            <Input
              placeholder="Target (optional, e.g. 3 leads)"
              value={bdTarget}
              data-testid="add-work-bd-target"
              onChange={(e) => setBdTarget(e.target.value)}
            />
          </>
        ) : null}

        {/*
          The brief has no maxLength and no character counter, by design (FORT-351): it lands in
          an unbounded TEXT column and some allocations need a long, detailed instruction. The API
          applies a 20,000-character sanity ceiling that no real brief approaches, so there is
          nothing useful to count down to here. Only the title / focus area above are capped for
          layout, because the work widget renders those.
        */}
        {workType === "Task" || workType === "BizDev" ? (
          <Input
            multiline
            rows={5}
            placeholder="Brief (optional) — the full instructions. Shown in full on Staff Daily; the work widget shows only the title."
            value={brief}
            data-testid="add-work-brief"
            onChange={(e) => setBrief(e.target.value)}
          />
        ) : null}

        {workType === "Sales" ? (
          <Text color="secondary">
            Sales work is assigned by picking leads in the CRM. Continue to the Membership CRM to select leads and
            assign the calls.
          </Text>
        ) : null}
      </Stack>
    </Modal>
  );
};

export default AddWorkModal;
